import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, NativeSelect } from "@/components/common/Page";
import { PhotoUpload } from "@/components/common/PhotoUpload";
import { Spinner } from "@/components/common/States";
import { api, errMsg } from "@/lib/api";
import { BILLING, nullify, todayISO } from "@/lib/format";

const EMPTY = {
  name: "", photo_path: null, birth_date: "", phone: "", email: "", guardian_name: "", guardian_phone: "", guardian_email: "", address: "",
  school: "", grade: "", subjects: [], notes: "", start_date: "", status: "ativo", billing_type: "por_aula",
};

export const StudentFormDialog = ({ open, onOpenChange, student, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [subjectsText, setSubjectsText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const base = student ? { ...EMPTY, ...Object.fromEntries(Object.entries(student).filter(([k]) => k in EMPTY).map(([k, v]) => [k, v ?? ""])) } : { ...EMPTY, start_date: todayISO() };
    setForm(base);
    setSubjectsText((student?.subjects || []).join(", "));
    setError("");
  }, [open, student]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const payload = { ...nullify(form), subjects: subjectsText.split(",").map((s) => s.trim()).filter(Boolean), photo_path: form.photo_path || null };
    try {
      const { data } = student ? await api.put(`/students/${student.id}`, payload) : await api.post("/students", payload);
      toast.success(student ? "Aluno atualizado" : "Aluno cadastrado com sucesso");
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="student-form-dialog">
        <DialogHeader><DialogTitle>{student ? "Editar aluno" : "Novo aluno"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <PhotoUpload value={form.photo_path} name={form.name} onChange={(p) => setForm((f) => ({ ...f, photo_path: p }))} testId="student-photo" />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nome completo *" className="sm:col-span-2">
              <Input value={form.name} onChange={set("name")} required minLength={2} data-testid="student-name-input" />
            </Field>
            <Field label="Telefone"><Input value={form.phone} onChange={set("phone")} data-testid="student-phone-input" /></Field>
            <Field label="E-mail"><Input type="email" value={form.email} onChange={set("email")} data-testid="student-email-input" /></Field>
            <Field label="Data de nascimento"><Input type="date" value={form.birth_date} onChange={set("birth_date")} data-testid="student-birth-input" /></Field>
            <Field label="Data de início"><Input type="date" value={form.start_date} onChange={set("start_date")} data-testid="student-start-input" /></Field>
            <Field label="Responsável"><Input value={form.guardian_name} onChange={set("guardian_name")} data-testid="student-guardian-input" /></Field>
            <Field label="Telefone do responsável"><Input value={form.guardian_phone} onChange={set("guardian_phone")} data-testid="student-guardian-phone-input" /></Field>
            <Field label="E-mail do responsável" hint="Recebe lembretes de aula e avisos de mensalidade atrasada" className="sm:col-span-2"><Input type="email" value={form.guardian_email} onChange={set("guardian_email")} data-testid="student-guardian-email-input" /></Field>
            <Field label="Escola"><Input value={form.school} onChange={set("school")} data-testid="student-school-input" /></Field>
            <Field label="Série/ano"><Input value={form.grade} onChange={set("grade")} data-testid="student-grade-input" /></Field>
            <Field label="Disciplinas" hint="Separe por vírgula. Ex.: Matemática, Física" className="sm:col-span-2">
              <Input value={subjectsText} onChange={(e) => setSubjectsText(e.target.value)} data-testid="student-subjects-input" />
            </Field>
            <Field label="Endereço (opcional)" className="sm:col-span-2"><Input value={form.address} onChange={set("address")} data-testid="student-address-input" /></Field>
            <Field label="Forma de cobrança">
              <NativeSelect value={form.billing_type} onChange={set("billing_type")} data-testid="student-billing-select">
                {Object.entries(BILLING).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </NativeSelect>
            </Field>
            <Field label="Status">
              <NativeSelect value={form.status} onChange={set("status")} data-testid="student-status-select">
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </NativeSelect>
            </Field>
            <Field label="Observações" className="sm:col-span-2"><Textarea value={form.notes} onChange={set("notes")} rows={3} data-testid="student-notes-input" /></Field>
          </div>
          {error && <p className="text-sm text-destructive" data-testid="student-form-error">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="student-form-cancel">Cancelar</Button>
            <Button type="submit" disabled={busy} data-testid="student-form-submit">{busy && <Spinner className="mr-2" />}Salvar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
